import React, { useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice } from "../../_layout";
import { formatDayMonth } from "../../utils/dateUtils";

const BodyweightCard = observer(({ client }) => {
  const [showBodyweightDetails, setShowBodyweightDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();

  if (!client.summaryStartDate) return null;

  const summaryStartDate = client.summaryStartDate;
  const weekEnd = new Date(summaryStartDate);
  weekEnd.setDate(weekEnd.getDate() + 7);

  const prevWeekStart = new Date(summaryStartDate);
  prevWeekStart.setDate(prevWeekStart.getDate() - 7);
  const prevWeekEnd = summaryStartDate;

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Build week days array
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(summaryStartDate);
    date.setDate(date.getDate() + i);
    weekDays.push({
      dayName: dayNames[date.getDay()],
      date: date,
      val: null,
    });
  }

  // Map bodyweights to the current week days
  if (client.bodyweights) {
    client.bodyweights.forEach((bw) => {
      if (!bw.date) return;
      const bwDate = new Date(bw.date);
      weekDays.forEach((day) => {
        if (
          bwDate.getFullYear() === day.date.getFullYear() &&
          bwDate.getMonth() === day.date.getMonth() &&
          bwDate.getDate() === day.date.getDate()
        ) {
          day.val = bw.bodyweight;
        }
      });
    });
  }

  // Calculate current week average
  const currentWeekValues = weekDays
    .filter((day) => day.val !== null)
    .map((day) => day.val);
  const currentWeekAverage =
    currentWeekValues.length > 0
      ? currentWeekValues.reduce((sum, val) => sum + val, 0) /
        currentWeekValues.length
      : null;

  // Calculate previous week average
  const prevWeekValues = [];
  if (client.bodyweights) {
    client.bodyweights.forEach((bw) => {
      if (!bw.date) return;
      const bwDate = new Date(bw.date);
      if (bwDate >= prevWeekStart && bwDate < prevWeekEnd) {
        prevWeekValues.push(bw.bodyweight);
      }
    });
  }
  const prevWeekAverage =
    prevWeekValues.length > 0
      ? prevWeekValues.reduce((sum, val) => sum + val, 0) /
        prevWeekValues.length
      : null;

  // Calculate difference
  const difference =
    currentWeekAverage !== null && prevWeekAverage !== null
      ? currentWeekAverage - prevWeekAverage
      : null;

  // Round to 1 decimal for comparison to avoid showing tiny differences as trends
  const roundedDifference =
    difference !== null ? parseFloat(difference.toFixed(1)) : null;

  const isIncreasing = roundedDifference !== null && roundedDifference > 0;
  const isDecreasing = roundedDifference !== null && roundedDifference < 0;
  const isNoChange = roundedDifference !== null && roundedDifference === 0;
  const trendColor = isIncreasing
    ? Colors.proteinColor
    : isDecreasing
    ? Colors.redColor
    : Colors.grayColor;
  const trendBg = isIncreasing
    ? "rgba(67, 160, 71, 0.1)"
    : isDecreasing
    ? "rgba(240, 68, 56, 0.1)"
    : Colors.lightGrayColor;

  // Get the unit based on client preference
  const weightUnit = client.bodyweightUnit ? "lbs" : "kg";

  // Find latest past week average if no current week data
  let latestPastWeekAverage = null;
  let latestPastWeekStart = null;
  if (currentWeekAverage === null && client.bodyweights) {
    // Go backwards week by week to find the most recent week with data
    for (let weeksBack = 1; weeksBack <= 12; weeksBack++) {
      const pastWeekStart = new Date(summaryStartDate);
      pastWeekStart.setDate(pastWeekStart.getDate() - weeksBack * 7);
      const pastWeekEnd = new Date(pastWeekStart);
      pastWeekEnd.setDate(pastWeekEnd.getDate() + 7);

      const pastWeekValues = [];
      client.bodyweights.forEach((bw) => {
        if (!bw.date) return;
        const bwDate = new Date(bw.date);
        if (bwDate >= pastWeekStart && bwDate < pastWeekEnd) {
          pastWeekValues.push(bw.bodyweight);
        }
      });

      if (pastWeekValues.length > 0) {
        latestPastWeekAverage =
          pastWeekValues.reduce((sum, val) => sum + val, 0) /
          pastWeekValues.length;
        latestPastWeekStart = pastWeekStart;
        break;
      }
    }
  }

  return (
    <View
      style={[
        GlobalStyles.Card,
        styles.halfCard,
        {
          minHeight: showBodyweightDetails ? 220 : "auto",
        },
      ]}
    >
      <View style={GlobalStyles.CardHeader}>
        <View style={GlobalStyles.CardHeaderMain}>
          <View style={GlobalStyles.CardIcon}>
            <Ionicons name="scale-outline" size={18} color={Colors.grayColor} />
          </View>
          <Text style={GlobalStyles.CardTitle}>
            {t("bodyweightTitle-string")}
          </Text>
        </View>
        <TouchableOpacity
          onPress={() => setShowBodyweightDetails(!showBodyweightDetails)}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={styles.iconButton}
        >
          <Ionicons
            name={showBodyweightDetails ? "analytics-outline" : "list"}
            size={16}
            color={isHovered ? Colors.darkgrayColor : Colors.grayColor}
          />
        </TouchableOpacity>
      </View>

      {showBodyweightDetails ? (
        <View style={styles.bwListContainer}>
          {weekDays.map((item, index) => (
            <View key={index} style={styles.bwRow}>
              <Text style={styles.bwDayText}>{item.dayName}</Text>
              <Text
                style={[
                  styles.bwValueText,
                  !item.val && styles.bwValuePlaceholder,
                ]}
              >
                {item.val ? `${item.val.toFixed(1)} ${weightUnit}` : "—"}
              </Text>
            </View>
          ))}
        </View>
      ) : (
        <View style={{ justifyContent: "center", flex: 1 }}>
          <View style={styles.bodyweightMain}>
            {currentWeekAverage !== null ? (
              <>
                <Text style={styles.bwLabel}>{t("weeklyAverage-string")}</Text>
                <View style={styles.bwTopRow}>
                  <Text style={styles.bwCurrent}>
                    {currentWeekAverage.toFixed(1)}
                  </Text>
                  <Text style={styles.bwUnit}>{weightUnit}</Text>
                </View>
                {roundedDifference !== null && (
                  <View style={styles.bwTrendRow}>
                    <View
                      style={[
                        styles.bwTrendBadge,
                        { backgroundColor: trendBg },
                      ]}
                    >
                      {!isNoChange && (
                        <Ionicons
                          name={
                            isIncreasing
                              ? "arrow-up"
                              : isDecreasing
                              ? "arrow-down"
                              : "remove"
                          }
                          size={12}
                          color={trendColor}
                        />
                      )}
                      <Text style={[styles.bwTrendText, { color: trendColor }]}>
                        {isNoChange
                          ? `= 0.0 ${weightUnit}`
                          : `${Math.abs(roundedDifference).toFixed(
                              1
                            )} ${weightUnit}`}
                      </Text>
                    </View>
                    <Text style={styles.bwTrendLabel}>
                      {t("vsPriorWeek-string")}
                    </Text>
                  </View>
                )}
              </>
            ) : latestPastWeekAverage !== null ? (
              <>
                <Text style={styles.bwLabel}>
                  {t("lastWeeklyAverage-string")}
                </Text>
                <View style={styles.bwTopRow}>
                  <Text style={[styles.bwCurrent, { color: Colors.grayColor }]}>
                    {latestPastWeekAverage.toFixed(1)}
                  </Text>
                  <Text style={styles.bwUnit}>{weightUnit}</Text>
                </View>
                <Text style={styles.bwMetaText}>
                  {t("weekOf-string")}{" "}
                  {formatDayMonth(latestPastWeekStart, i18n.language || "en")}
                </Text>
              </>
            ) : (
              <View style={styles.bwEmptyState}>
                <Text style={styles.bwEmptyTitle}>{t("noDataYet-string")}</Text>
                <Text style={styles.bwEmptyDescription}>
                  {t("bodyweightTrendsInfo-string")}
                </Text>
              </View>
            )}
          </View>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  halfCard: {
    flex: 1,
    minWidth: 240,
  },
  iconButton: {
    padding: 4,
  },
  bodyweightMain: {
    gap: 4,
  },
  bwLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  bwTopRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 4,
  },
  bwCurrent: {
    fontSize: 28,
    fontWeight: "600",
    color: Colors.blackColor,
    letterSpacing: -0.5,
  },
  bwUnit: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  bwTrendRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
  },
  bwTrendBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: "rgba(240, 68, 56, 0.1)",
  },
  bwTrendText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.redColor,
  },
  bwTrendLabel: {
    fontSize: 11,
    color: Colors.grayColor,
  },
  bwMetaText: {
    fontSize: 11,
    color: Colors.grayColor,
    marginTop: 4,
  },
  bwEmptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
    gap: 8,
  },
  bwEmptyTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  bwEmptyDescription: {
    fontSize: 11,
    color: Colors.grayColor,
    textAlign: "center",
    maxWidth: 200,
  },
  bwListContainer: {
    gap: 6,
    paddingTop: 4,
  },
  bwRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  bwDayText: {
    fontSize: 12,
    color: Colors.grayColor,
    fontWeight: "500",
  },
  bwValueText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "600",
  },
  bwValuePlaceholder: {
    color: Colors.grayColor,
    opacity: 0.5,
  },
});

export default BodyweightCard;
